import { IAd } from '.'
import { AdService } from '..'
import { DOMUtils } from '../../../utils/dom'
import { StickyAd } from './sticky'

export class ScrollingFollow {

    public static active = false

    public static readonly headerMoveDuration = 800

    private static updateFunctions: { [name: string]: () => void } = {}
    private static curTimeouts: NodeJS.Timeout[] = []
    private static headerShrinkState = false
    private static extraHeight = 0
    private static disabledPlacements: string[] = []
    private static headerUpdateInterval = 10
    private static heightOffset = 100

    /**
     * Sets internal variables based on the settings passed in (currently in A/B test stage)
     * including whether it is active or not.
     * @param conf A scrollingFollow configuration
     */
    public static applySettings(conf: any): void {
        if (!conf) {
            return
        }
        if (conf.enabled) {
            if (window.innerWidth < DOMUtils.mobileCutoff && conf.enabledMobile !== false) {
                this.active = true
            } else if (window.innerWidth >= DOMUtils.mobileCutoff && conf.enabledDesktop !== false) {
                this.active = true
            }
        }
        if (conf.extraHeight > 0) {
            this.extraHeight = conf.extraHeight
        }
        if (conf.disabledPlacements) {
            this.disabledPlacements = conf.disabledPlacements.map((i: number) => `ad-${i}`)
        }
        if (conf.headerUpdateInterval) {
            this.headerUpdateInterval = conf.headerUpdateInterval
        }
    }

    /**
     * Updates height of the ad container, then creates an update function for the ad, appends it to the dict,
     * and ads the function to an event listener for scroll events
     * @param adObj An ad object
     */
    public static enableScrollingFollow(adObj: IAd): void {
        const adId = adObj.id
        // Scrolling Follow has already been enabled or is disabled for this ad
        if (this.disabledPlacements.includes(adId) || adId in this.updateFunctions ||
            StickyAd.activeTop && adId === AdService.DOM.ids.loc1) {
            return
        }
        const curHeight = adObj.getMaxHeight()

        const ad = document.getElementById(adId) as HTMLElement
        const adCont = ad.parentElement as HTMLElement
        ad.removeAttribute('style')
        let adContHeight = curHeight
        if (adId !== AdService.DOM.ids.loc1) {
            adContHeight += this.extraHeight
        }
        adCont.style.height = `${adContHeight}px`

        ScrollingFollow.updateFunctions[adId] = ScrollingFollow.getUpdateFunction(adId)

        window.addEventListener('scroll', ScrollingFollow.updateFunctions[adId])
    }

    /**
     * Calls update function for all ads that have Scrolling Follow enabled.
     */
    public static updateAllAds() {
        for (const func of Object.values(this.updateFunctions)) {
            func()
        }
    }

    /**
     * Sets the height of an ad depending on its position relative to the header
     * @param ad The ad div
     * @param adCont The parent of the ad div
     * @param headerHeight The current height of the page header
     */
    public static setAdHeight(ad: HTMLElement, adCont: HTMLElement, headerHeight: number) {
        const adContRect = adCont.getBoundingClientRect()

        // Total space showing in ad container is less than ad size
        if (adContRect.bottom - headerHeight < ad.offsetHeight) {
            ad.style.position = 'absolute'
            ad.style.top = ''
            ad.style.bottom = '0px'
        } else if (adContRect.top < headerHeight) {  // The ad container is partially under the header
            ad.style.position = 'fixed'
            ad.style.top = `${headerHeight}px`
            ad.style.bottom = ''
        } else {  // The ad container has not yet hit the top of the page
            ad.style.position = 'absolute'
            ad.style.top = '0px'
            ad.style.bottom = ''
        }
    }

    /**
     * @returns whether or not the header has shrunk/expanded since last call. Necessary for mobile.
     */
    public static detectHeaderChanged() {
        const headerShrink = document.getElementsByTagName('html')[0].
        classList.contains(AdService.DOM.classes.headerShrink)
        if (ScrollingFollow.headerShrinkState !== headerShrink) {  // if header shrink status is changed
            ScrollingFollow.headerShrinkState = headerShrink
            return true
        }
        return false
    }

    /**
     * Returns the y coordinate of the bottom of the page header, so the scrolling follow ad becomes sticky
     * at the correct height. The bottom of the header is one of three possible DOM elements: the masthead,
     * the subscribe button (when width < 768px), or the sticky banner ad.
     * @returns the y value of the bottom of the page header
     */
    public static getHeaderHeight(): number {
        let headerHeight: number
        if (StickyAd.isActiveTop()) {
            headerHeight = document.querySelector(`.${AdService.DOM.classes.stickyAd}`)
                            .getBoundingClientRect().bottom
        } else {
            const subscribe = document.querySelector(`.${AdService.DOM.classes.subscribe}`)
            const header = document.getElementById(AdService.DOM.ids.header)
            if (subscribe) {
                headerHeight = Math.max(subscribe.getBoundingClientRect().bottom,
                                        header.getBoundingClientRect().bottom)
            } else {
                headerHeight = header.getBoundingClientRect().bottom
            }
        }
        return headerHeight
    }

    /**
     *
     * @param adId The id of an ad div on the page
     * @returns a function that finds a specific ad and updates its height within its container
     */
    private static getUpdateFunction(adId: string) {
        return () => {
            const ad = document.getElementById(adId) as HTMLElement
            const adCont = ad.parentElement as HTMLElement

            if (ad && adCont) {
                const headerHeight = this.getHeaderHeight()
                const adContRect = adCont.getBoundingClientRect()
                if (window.innerWidth < DOMUtils.mobileCutoff) {
                    // if the bottom of the add is still below the header (with an additional 100px for safety)
                    // and the top of the ad is covered by the header (with an additional 100px for safety)
                    // and the page has just shrunk/expanded, ensure that the ad follows the header
                    if (adContRect.bottom - headerHeight > -this.heightOffset &&
                        adContRect.top - this.heightOffset < headerHeight &&
                        this.detectHeaderChanged()) {
                        this.setAdHeightDelayed(adId)
                    }
                }

                this.setAdHeight(ad, adCont, headerHeight)
            }
        }
    }

    /**
     * Sets timeouts ensuring that the ad will stick to the header even if it moves
     * Necessary for mobile when the header will shrink or expand in response to scrolling for 800ms
     * @param adId The id of an ad div on the page
     */
    private static setAdHeightDelayed(adId: string) {
        const ad = document.getElementById(adId) as HTMLElement
        const adCont = ad.parentElement as HTMLElement

        for (const timeout of this.curTimeouts) {  // remove existing timeouts
            clearTimeout(timeout)
        }
        this.curTimeouts = []
        const refreshNum = this.headerMoveDuration / this.headerUpdateInterval
        for (let i = 1; i <= refreshNum; i++) {  // set timeouts for next 800 ms
            const timeout = setTimeout(() => {
                ScrollingFollow.setAdHeight(ad, adCont, this.getHeaderHeight())
            }, i * this.headerUpdateInterval)
            this.curTimeouts.push(timeout)
        }
       //
    }
}
